import React, { useRef, useState } from "react";
import { DesignerState, useDesigner } from "./useDesigner";
import { useChannel } from "../core/hooks/useChannel";
import { DesignerMessages, getTypeName } from "./types";
import { Content, Group, Heading, Img, Paragraph, Region, Section, Table, TableCell, TableRow, TOCLevelFormat, PdfDefaults, HeadingStyle } from "../core/pdf/types";
import { Table as UITable } from "../core/ui/Table";
import { DataContext, DataMessages, useData, useDataContext, useDataEvents, useDataValue } from "../core/hooks/useData";
import { Checkbox } from "../core/form/Checkbox";
import { Text, TextProps } from "../core/form/Text";
import { toMillimeters, toPdfPoint } from "../core/pdf/utils";
import { Icon } from "../core/ui/icons";
import { JSEditor } from "../core/ui/JSEditor";

import { OPS } from 'pdfjs-dist';
import { newID } from "../core/utils/unique";
import { Modal } from "../core/ui/Modal";
import { getPresenter } from "../data-entry/Presenter";
import { api } from "../api/api";
import { InlinePresenter } from "../data-entry/InlinePresenter";
import { ConditionalController, useConditional } from "../core/hooks/useConditional";
import { getImgSize } from "../core/utils/dom";
import { moveArrayElement } from "../core/utils/array";
import { useRefresh } from "../core/hooks/useRefresh";
import { isArray, isObject, toNumber, isValidNumber } from "rant-utils";
import { ElementState } from "./useElementContext";


function duplicateElement(designer: DesignerState, srcRoot: Content) {

	const destRoot = JSON.parse(JSON.stringify(srcRoot));
	function replaceIDs(src: any) {
		if (src.id) src.id = designer.nextID();
		for(let m in src) {
			const v = src[m];

			if (isArray(v)) {
				for(let a of v) {
					replaceIDs(a);
				}
			} else if (isObject(v)) {
				replaceIDs(v);
			} 
		}
	}
	replaceIDs(destRoot);

	return destRoot;

}


function TocLevelModal(props: {
	level: TOCLevelFormat,
	levelIndex: number,
	onClose: () => void,
}) {
	const title = props.levelIndex === 0 ? "TOC Default" : `TOC Level ${props.levelIndex}`;
	const data = useDataContext({ initialValue: props.level });

	return (
		<DataContext.Provider value={data}>
			<Modal
				title={title}
				size="width-40vw"
				showCancel={false}
				saveTitle="Close"
				onSave={props.onClose}
			>
				<NumberValue prop="fontSize" title="Font size" />
				<Checkbox prop="bold" title="Bold" />
				<Checkbox prop="italic" title="Italic" />
				<Checkbox prop="uppercase" title="Uppercase" />
				<Checkbox prop="smallCaps" title="Small caps" />
				<MillimeterValue prop="marginLeft" title="Left margin" />
				<MillimeterValue prop="marginTop" title="Top margin" />
				<MillimeterValue prop="marginBottom" title="Bottom margin" />
				<MillimeterValue prop="listNumberIndent" title="Number indent" />
			</Modal>
		</DataContext.Provider>
	);
}


function HeadingLevelModal(props: {
	heading: HeadingStyle,
	levelIndex: number,
	onClose: () => void,
}) {
	const title = props.levelIndex === 0 ? "Heading Default" : `Heading Level ${props.levelIndex}`;
	const data = useDataContext({ initialValue: props.heading });

	return (
		<DataContext.Provider value={data}>
			<Modal
				title={title}
				size="width-40vw"
				showCancel={false}
				saveTitle="Close"
				onSave={props.onClose}
			>
				<NumberValue prop="fontSize" title="Font size" />
				<Checkbox prop="bold" title="Bold" />
				<Checkbox prop="italic" title="Italic" />
				<Checkbox prop="smallCaps" title="Small caps" />
				<Text prop="textAlign" title="Text alignment" />
				<MarginValue prop="margin" />
			</Modal>
		</DataContext.Provider>
	);
}


function DocumentDefaults(props: {
	designer: DesignerState,
	controller: ConditionalController<any>
}) {

	/*
	pageSize: Size,
	margin: Margin,
	headings: HeadingStyle[],

	fontName: string, // "helvetica"
	fontSize: number,
	color: Color,
	lineHeight: number, // NOTE: browser only seems to support to one decimal point
	
	listIndent: number,
	listStyle: string,	
	*/

	const defaults = props.designer.structure.defaults;

	if (!defaults.tocLevelFormats) {
		defaults.tocLevelFormats = JSON.parse(JSON.stringify(PdfDefaults.tocLevelFormats));
	}

	defaults.headings.forEach((h: HeadingStyle) => {
		if (!h.margin) h.margin = { top: 0, right: 0, bottom: 0, left: 0 };
	});

	const data = useDataContext({
		initialValue: defaults,
	});

	const levelModal = useConditional((levelData: { level: TOCLevelFormat, levelIndex: number }) => (
		<TocLevelModal level={levelData.level} levelIndex={levelData.levelIndex} onClose={levelModal.close} />
	));

	const headingModal = useConditional((headingData: { heading: HeadingStyle, levelIndex: number }) => (
		<HeadingLevelModal heading={headingData.heading} levelIndex={headingData.levelIndex} onClose={headingModal.close} />
	));

	return (
		<DataContext.Provider value={data}>
			<Modal
				title={"Document Defaults"}
				size="width-50vw"

				showCancel={false}
				saveTitle="Close"
				onSave={() => {
					props.controller.close();
				}}
			>
				<Text prop="fontSize" title="Font size" />
				<Text prop="fontName" title="Font name" />
				<Text prop="lineHeight" title="Line height" />
				<Text prop="baselineAdjust" title="Baseline adjust" />
				<Text prop="textAlign" title="Text alignment" />

				<div className="flex horiz align-start gap-10 margin-top-5">
					<MarginValue prop="margin" />
					<MarginValue prop="cellPadding" type="Table cell padding"  />
				</div>

				<div className="prop-group margin-top-10">
					<b>TOC Level Formats</b>
					<UITable
						interactive
						style={{ marginTop: 6, marginBottom: 6 }}
						items={defaults.tocLevelFormats!.map((level: TOCLevelFormat, i: number) => ({ level, levelIndex: i }))}
						cols={[
							{ title: "Level", value: ({ levelIndex }: any) => levelIndex === 0 ? "Default" : `Level ${levelIndex}` },
							{ title: "Font size", className: "align-center", value: ({ level }: any) => level.fontSize ?? "—" },
							{ title: "Bold", className: "align-center", value: ({ level }: any) => level.bold ? <Icon type="tick" size="small" /> : "" },
							{ title: "Italic", className: "align-center", value: ({ level }: any) => level.italic ? <Icon type="tick" size="small" /> : "" },
							{ title: "Uppercase", className: "align-center", value: ({ level }: any) => level.uppercase ? <Icon type="tick" size="small" /> : "" },
							{ title: "Small caps", className: "align-center", value: ({ level }: any) => level.smallCaps ? <Icon type="tick" size="small" /> : "" },
							{ title: "Top margin", className: "align-right", value: ({ level }: any) => level.marginTop ? `${Math.round(toMillimeters(level.marginTop) * 10) / 10}mm` : "—" },
							{ title: "Bot margin", className: "align-right", value: ({ level }: any) => level.marginBottom ? `${Math.round(toMillimeters(level.marginBottom) * 10) / 10}mm` : "—" },
							{ title: "Left margin", className: "align-right", value: ({ level }: any) => `${Math.round(toMillimeters(level.marginLeft) * 10) / 10}mm` },
							{ title: "Num indent", className: "align-right", value: ({ level }: any) => `${Math.round(toMillimeters(level.listNumberIndent) * 10) / 10}mm` },
						]}
						onClick={(_e, r) => levelModal.show(r)}
					/>
				</div>

				<div className="prop-group margin-top-10">
					<b>Heading Formats</b>
					<UITable
						interactive
						style={{ marginTop: 6, marginBottom: 6 }}
						items={defaults.headings.map((heading: HeadingStyle, i: number) => ({ heading, levelIndex: i }))}
						cols={[
							{ title: "Level", value: ({ levelIndex }: any) => levelIndex === 0 ? "Default" : `Level ${levelIndex}` },
							{ title: "Font size", className: "align-center", value: ({ heading }: any) => heading.fontSize ?? "—" },
							{ title: "Bold", className: "align-center", value: ({ heading }: any) => heading.bold ? <Icon type="tick" size="small" /> : "" },
							{ title: "Italic", className: "align-center", value: ({ heading }: any) => heading.italic ? <Icon type="tick" size="small" /> : "" },
							{ title: "Small caps", className: "align-center", value: ({ heading }: any) => heading.smallCaps ? <Icon type="tick" size="small" /> : "" },
							{ title: "Top margin", className: "align-right", value: ({ heading }: any) => heading.margin?.top ? `${Math.round(toMillimeters(heading.margin.top) * 10) / 10}mm` : "—" },
							{ title: "Bot margin", className: "align-right", value: ({ heading }: any) => heading.margin?.bottom ? `${Math.round(toMillimeters(heading.margin.bottom) * 10) / 10}mm` : "—" },
							{ title: "Align", className: "align-center", value: ({ heading }: any) => heading.textAlign ?? "—" },
						]}
						onClick={(_e, r) => headingModal.show(r)}
					/>
				</div>
			</Modal>

			{levelModal.render()}
			{headingModal.render()}

		</DataContext.Provider>
	)
}

interface ClipboardContents {
	action: "cut" | "copy" | undefined,
	items: ElementState[],
}

export function Inspector(props: {

}) {

	const designer = useDesigner();

	const [focused, setFocused] = React.useState<Content | undefined>();

	const pageRects = useRef([] as any);

	const clipboardContents = useRef({
		action: undefined,
		items: [],
	} as ClipboardContents);

	useChannel(designer.channel, React.useCallback((msg) => {
		if (msg.type === DesignerMessages.FOCUS_CHANGED) {
			setFocused(designer.focus);
		
		} else if (msg.type === DesignerMessages.ADD_REGION) {			
			// make sure a section is still focused
			if (!designer.focus || designer.focus.type !== "section") return;

			// is there a rect at this position?
			const { x, y } = msg.point;
			debugger;
			for(let r of pageRects.current) {
				if (
					x >= r.x && x <= (r.x+r.w)
					&& y >= r.y && y <= (r.y+r.h)
				) {
					const region = designer.createItem("region") as Region;

					region.position = {
						x: r.x,
						y: r.y,
					};
					region.size = {
						w: r.w,
						h: r.h,
					};

					const section = designer.focus as Section;
					section.content.push(region);
		
					// refresh section
					designer.refreshItem(section);

					break;
				}
			}
			debugger;

		} 
		
	}, [designer]));

	const [, refresh] = React.useState({});

	const dataContext = useDataContext({ initialValue: {} as any });

	const docDefaultsModal = useConditional(() => {
		return (
			<DocumentDefaults
				designer={designer}
				controller={docDefaultsModal}
			/>
		);
	});

	if (!focused) return null;

	const elementContext = designer.getContext(focused.id!);

	// always update the data state to reference the correct object
	// deliberately not recreating continually unnecessarily
	dataContext.value = focused;

	const fields: any[] = [];
	const buttons: any[] = [];

	let hasDelete = false;

	// console.log("focused type: " + focused.type);
	fields.push(
		<div className="prop-group">
			<b>Visibility criteria</b>
			<div>
				<JSEditor prop="visibility" key="visibility" />
			</div>
		</div>
	);

	fields.push(
		<div className="prop-group">
			<b>Styling</b>
			<div>
				<TextValue prop="className" title="Css Class" key="className" />
				{
					(focused.type === "section") &&
					<BorderValue prop="border" />
				}
				{
					((focused.type === "p" || focused.type === "heading")) &&
					<>
						<AlignValue item={focused} prop="align" />
						<Checkbox prop="bold" title="Bold" />
						<Checkbox prop="italic" title="Italic" />
						<Checkbox prop="smallCaps" title="Small caps" />
						<NumberValue prop="fontSize" title="Font size" />
						{
							(focused.type === "heading") &&
							<HeadingLevelValue item={focused as Heading} />
						}
					</>
				}
				{
					(focused.type === "table") && 
					<Checkbox key="hideBorders" prop="hideBorders" title="Hide borders" />
				}
				{
					(focused.type === "group") && 
					<>
						<Checkbox prop="keepTogether" title="Keep together" />
						<MarginValue prop="margin" />
					</>
				}
				{
					(focused.type === "list") &&
					<Checkbox prop="ignoreFormatOneItem" title="Ignore list formatting if only one item" />
				}
			</div>
		</div>
	)


	if (focused.type === "section") {
		const s = focused as Section;
		fields.push(
			<Checkbox prop="continuous" title="Continuous" key="continuous" />,
			<Checkbox prop="pageBreakBefore" title="Page break before" key="pageBreakBefore" />,
			<Checkbox prop="landscape" title="Landscape" key="landscape" />,
			<MillimeterValue prop="fixedHeight" title="Fixed height" key="fixedHeight" />,
			<MarginValue prop="margin" key="margin" />,
		);

		buttons.push(
			<button
				key="document-defaults"
				className="primary"
				onClick={() => {
					docDefaultsModal.show({});
				}}
			>Document Defaults</button>
		);

		if (!s.header) {
			buttons.push(
				<button
					key="add-header"
					className="primary"
					onClick={() => {
						const p = designer.createItem("p") as Paragraph;
						s.header = {
							id: designer.nextID(),
							type: "header",
							content: [p],
							height: 50,
						}
						designer.setFocus(p);
						designer.refreshItem(s);
					}}
				>Add Header</button>
			)
		}
		if (!s.footer) {
			buttons.push(
				<button
					key="add-footer"
					className="primary"
					onClick={() => {
						const p = designer.createItem("p") as Paragraph;
						s.footer = {
							id: designer.nextID(),
							type: "footer",
							content: [p],
							height: 50,
						}
						designer.setFocus(p);
						designer.refreshItem(s);
					}}
				>Add Footer</button>
			)
		}

		if (designer.bgPdfPages) { 
			fields.push(
				<button
					onClick={() => {

						const section = focused as Section;
						const page = designer.bgPdfPages[section.pdfPageIndex!];

						const viewport = page.getViewport();
						const pageWidth = viewport.viewBox[2];
						const pageHeight = viewport.viewBox[3];

						const minHeight = toPdfPoint(4);
						const maxHeight = toPdfPoint(10);

						// page.getTextContent().then(function(textContent: any) {
						// 	debugger;
						// 	for(let tc of textContent.items) {
						// 		if (tc.str === "re") {
						// 			console.dir(tc);
						// 		}
						// 	}
						// });

						page.getOperatorList().then(function(operatorList: any) {

							const unique = [] as any[];

							const ops = {
								dependency: OPS.dependency,
								setLineWidth: OPS.setLineWidth,
								setLineCap: OPS.setLineCap,
								setLineJoin: OPS.setLineJoin,
								setMiterLimit: OPS.setMiterLimit,
								setDash: OPS.setDash,
								setRenderingIntent: OPS.setRenderingIntent,
								setFlatness: OPS.setFlatness,
								setGState: OPS.setGState,
								save: OPS.save,
								restore: OPS.restore,
								transform: OPS.transform,
								moveTo: OPS.moveTo,
								lineTo: OPS.lineTo,
								curveTo: OPS.curveTo,
								curveTo2: OPS.curveTo2,
								curveTo3: OPS.curveTo3,
								closePath: OPS.closePath,
								rectangle: OPS.rectangle,
								stroke: OPS.stroke,
								closeStroke: OPS.closeStroke,
								fill: OPS.fill,
								eoFill: OPS.eoFill,
								fillStroke: OPS.fillStroke,
								eoFillStroke: OPS.eoFillStroke,
								closeFillStroke: OPS.closeFillStroke,
								closeEOFillStroke: OPS.closeEOFillStroke,
								endPath: OPS.endPath,
								clip: OPS.clip,
								eoClip: OPS.eoClip,
								beginText: OPS.beginText,
								endText: OPS.endText,
								setCharSpacing: OPS.setCharSpacing,
								setWordSpacing: OPS.setWordSpacing,
								setHScale: OPS.setHScale,
								setLeading: OPS.setLeading,
								setFont: OPS.setFont,
								setTextRenderingMode: OPS.setTextRenderingMode,
								setTextRise: OPS.setTextRise,
								moveText: OPS.moveText,
								setLeadingMoveText: OPS.setLeadingMoveText,
								setTextMatrix: OPS.setTextMatrix,
								nextLine: OPS.nextLine,
								showText: OPS.showText,
								showSpacedText: OPS.showSpacedText,
								nextLineShowText: OPS.nextLineShowText,
								nextLineSetSpacingShowText: OPS.nextLineSetSpacingShowText,
								setCharWidth: OPS.setCharWidth,
								setCharWidthAndBounds: OPS.setCharWidthAndBounds,
								setStrokeColorSpace: OPS.setStrokeColorSpace,
								setFillColorSpace: OPS.setFillColorSpace,
								setStrokeColor: OPS.setStrokeColor,
								setStrokeColorN: OPS.setStrokeColorN,
								setFillColor: OPS.setFillColor,
								setFillColorN: OPS.setFillColorN,
								setStrokeGray: OPS.setStrokeGray,
								setFillGray: OPS.setFillGray,
								setStrokeRGBColor: OPS.setStrokeRGBColor,
								setFillRGBColor: OPS.setFillRGBColor,
								setStrokeCMYKColor: OPS.setStrokeCMYKColor,
								setFillCMYKColor: OPS.setFillCMYKColor,
								shadingFill: OPS.shadingFill,
								beginInlineImage: OPS.beginInlineImage,
								beginImageData: OPS.beginImageData,
								endInlineImage: OPS.endInlineImage,
								paintXObject: OPS.paintXObject,
								markPoint: OPS.markPoint,
								markPointProps: OPS.markPointProps,
								beginMarkedContent: OPS.beginMarkedContent,
								beginMarkedContentProps: OPS.beginMarkedContentProps,
								endMarkedContent: OPS.endMarkedContent,
								beginCompat: OPS.beginCompat,
								endCompat: OPS.endCompat,
								paintFormXObjectBegin: OPS.paintFormXObjectBegin,
								paintFormXObjectEnd: OPS.paintFormXObjectEnd,
								beginGroup: OPS.beginGroup,
								endGroup: OPS.endGroup,
								beginAnnotation: OPS.beginAnnotation,
								endAnnotation: OPS.endAnnotation,
								paintImageMaskXObject: OPS.paintImageMaskXObject,
								paintImageMaskXObjectGroup: OPS.paintImageMaskXObjectGroup,
								paintImageXObject: OPS.paintImageXObject,
								paintInlineImageXObject: OPS.paintInlineImageXObject,
								paintInlineImageXObjectGroup: OPS.paintInlineImageXObjectGroup,
								paintImageXObjectRepeat: OPS.paintImageXObjectRepeat,
								paintImageMaskXObjectRepeat: OPS.paintImageMaskXObjectRepeat,
								paintSolidColorImageMask: OPS.paintSolidColorImageMask,
							}
							const map = {} as any;
							for(let m in ops) {
								map[(ops as any)[m as any] as any] = m;
							}
							console.dir(map);

							const order = [] as any[];
							for (var i = 0; i < operatorList.fnArray.length; i++) {
								order.push({
									op: map[operatorList.fnArray[i]],
									args: operatorList.argsArray[i],
								});
							}
							// console.dir(order);

							const rects = [];

							for (var i = 0; i < operatorList.fnArray.length; i++) {
								var fn = operatorList.fnArray[i];
								var args = operatorList.argsArray[i];

								// if (args) {
								// 	if (unique.indexOf(args[0]) < 0) {
								// 		unique.push(args[0])
								// 	}
								// }

								if (
									(fn === 91) // construct path
									&& (args && args[0][0] === 19) // rect
								 ) {

									const dimensions = args[1];
									const a = {
										x: dimensions[0],
										y: dimensions[1],
										w: dimensions[2],
										h: dimensions[3],
									}

									if (a.w >= pageWidth || a.h >= pageHeight) {
										continue;
									}
									if (a.h > maxHeight) {
										continue;
									}
									if (a.h < minHeight) continue;

									debugger;

									// was the prev command a transform?
									if (operatorList.fnArray[i-1] === OPS.transform) {
										const tfArgs = operatorList.argsArray[i-1];
										
										const scale = tfArgs[0];
										a.x = (a.x * scale) + tfArgs[4];
										a.y = tfArgs[5];
										a.w *= scale;
										//a.h *= scale;

										debugger;
									}


									a.y = section.height! - a.y - a.h;


									// if (a.x < 10 && a.y >= section.height!) {
									// 	debugger;
									// }

									rects.push(a);


									// break;


								}

								if (fn === 14 || fn === 13 || fn === 19) {
									debugger;
								}
								if (unique.indexOf(fn) < 0) {
									unique.push(fn);
								}

							}


							pageRects.current = rects;
							// console.dir(unique);
							// for(let r of rects) {
							// 	// there is a rect that is the entire page
							// 	if (r.w > 500 || r.h > 500) continue;

							// 	const region = designer.createItem("region") as Region;

							// 	region.position = {
							// 		x: r.x,
							// 		y: r.y,
							// 	};
							// 	region.size = {
							// 		w: r.w,
							// 		h: r.h,
							// 	};
							// 	section.content.push(region);
					
							// 	// refresh section
							// 	designer.refreshItem(section);
					
							// }


						  });

					}}
				>Auto Create Fields</button>,
				<div><i>Use this to automatically determine areas of potential fields within the current page selected, then use CTRL+click to create a field at each location</i></div>
			);
		}
	
	} else if ((focused.type === "header") || (focused.type === "footer")) {
		fields.push(
			<MillimeterValue prop="height" title="Height" key="height" />,
			
			<Checkbox prop="hideFirstPage" title="Hide first page" key="hideFirstPage" />,

		);

		buttons.push(
			<button
				key="delete"
				className="primary"
				onClick={() => {
					const context = designer.getContext(focused.id!);
					if (!context) return;
					const parent = context.parent.def as any;

					delete parent[focused.type!];

					designer.refreshItem(parent);
					setFocused(undefined);
				}}
			>Delete</button>
		);

		// need custom delete for this
		

	} else if (focused.type === "list") {
		fields.push(
			<TextValue prop="style" title="Style" key="style" />,
			<NumberValue prop="start" title="Start" key="start" />,
			<Checkbox prop="keepItemsTogether" title="Keep Items Together" key="keepItemsTogether" />,

			<MarginValue prop="margin" key="margin" />,
			<MarginValue prop="itemMargin" key="itemMargin" type="Item Margin" />,

			<MillimeterValue prop="indent" title="Indent" key="indent" />,
									
		);
		hasDelete = true;

	} else if (focused.type === "pagebreak") {
		debugger;
		hasDelete = true;

	} else if (focused.type === "table") {

		const tbl = focused as Table;
		if (tbl) {
			
			const cols = [];

			for(let i=0;i<tbl.cols.length;i++) {
				const c = tbl.cols[i];
				if (!c.id) c.id = designer.nextID();

				cols.push(
					<PercentValue prop={`cols[${i}].widthPercent`} title={"Column " + (i+1)} key={c.id} />,
				);
			}

			fields.push(
				<div className="prop-group" key="cols">
					<b>Columns</b>
					<div>
						<TextValue prop="width" title="Width" key="width" />
						{cols}
					</div>
				</div>

			);

			fields.push(
				<MarginValue prop="margin" key="margin" />,
				<MarginValue prop="cellPadding" key="cellPadding" type="Cell Padding" />,
				<Checkbox key="allowBreak" prop="allowBreak" title="Allow break page" />,
			);

		}
		hasDelete = true;

	} else if (focused.type === "tableCell") {
		fields.push(
			<StandardColors key="standardColors" />,
			<MarginValue prop="cellPadding" key="cellPadding" type="Cell Padding" />,
		);

	} else if (focused.type === "p" || focused.type === "heading") {

		fields.push(
			<div className="prop-group" key="layout">
				<b>Layout</b>
				<div>
					<Checkbox key="overflow" prop="overflow" title="Overflow" />
					<Checkbox key="continue" prop="continue" title="Continue Previous Line" />
				</div>
			</div>,

			<div className="prop-group" key="script-events">
				<b>Script events</b>
				<div>
					<JSEditor prop="scriptEvents" key="scriptEvents" />
				</div>
			</div>
		);

	} else if (focused.type === "region") {
		hasDelete = true;

	} else if (focused.type === "image") {
		const img = focused as Img;

		hasDelete = true;

		fields.push(
			<ImageValue
				prop="image"
				key="image"
			/>,
			<NumberValue prop="width" title="Width" key="width" />,
			<NumberValue prop="height" title="Height" key="height" />,
			<PercentValue prop="scale" title="Scale" key="scale" />,
			<button
				key="reset"
				onClick={() => {
					const url = api.getDownloadSystemUrl(img.image.blob);
					getImgSize(url).then((sz: any) => {
						img.width = sz.width;
						img.height = sz.height;

						designer.refreshItem(img);
						// also refresh the designer
						refresh({});
					})
				}}
			>Reset Dimensions</button>,

			<AlignValue key="align" item={focused} prop="align" />,

		);

	} else if (focused.type === "sub-report") {
		hasDelete = true;

	} else if (focused.type === "sig-block") {
		hasDelete = true;
		fields.push(
			<label key="entity-label">Entity for sig:</label>,
			<JSEditor prop="entity" key="entity" />,
			<TextValue prop="layout" key="layout" title="Layout" />,
			<Checkbox prop="retainFormatting" key="retainFormatting" title="Retain Formatting" />,
			<MillimeterValue prop={"sigHeight"} title="Signature Height" key={"sigHeight"} />

		);

	} else if (focused.type === "toc") {
		hasDelete = true;
		fields.push(
			<Checkbox prop="allowLevel1Break" key="allowLevel1Break" title="Allow level 1 break" />,
			<label key="entity-label">Schedules (JS Array):</label>,
			<JSEditor prop="endItems" key="endItems" />,
		);

	} else if (focused.type === "tag") {
		hasDelete = true;

	} else if (focused.type === "link") {
		hasDelete = true;

		fields.push(
			<TextValue prop="title" key="title" title="Title" />,
			<TextValue prop="url" key="url" title="Url" />,
		);

	}


	if (
		(focused.type === "group")
		|| (focused.type === "tableRow")
		|| (focused.type === "listItem")
		|| (focused.type === "section")
	) {
		hasDelete = true;
		fields.push(
			<div className="prop-group" key="repeat">
				<b>Repeat</b>
				<div>
					<TextValue prop="list" title="List to iterate" />
					<TextValue prop="lookupContainer" title="Lookup container" />
					<TextValue prop="scopeName" title="Scope name" />
					<TextValue prop="counterName" title="Counter name" />
					<Checkbox prop="newPage" title="New page each repeat" />
					<Checkbox prop="newPageSkipFirst" title="Skip first new page" />
					<Checkbox prop="repeatToPageBottom" title="Repeat to page bottom" />

					<div className="prop-group">
						<b>Filter criteria</b>
						<div><JSEditor prop="filter" key="filter" /></div>
					</div>
				</div>
			</div>,

			<Checkbox prop="pageBreakBefore" title="Page break before" />

		)
	}


	function checkForParent(type: string) {
		let o = elementContext;
		while (o && o.def) {
			if (o.def.type == type) return o;
			o = o.parent;
		}
		return undefined;
	}

	// check through parents to see if have a table
	const rowContext = checkForParent("tableRow");
	if (rowContext) {
		buttons.push(
			<button
				key="delete-row"
				className="primary"
				onClick={() => {
					const row = rowContext.def as TableRow;
					const tbl = rowContext.parent.def as Table;

					const i = tbl.rows.indexOf(row);
					if (tbl.rows.length === 1) {
						// delete whole table
						const parent = rowContext.parent.parent.def as any;
						const tblIndex = parent.content.indexOf(tbl);
						parent.content.splice(tblIndex, 1);
						if (parent.content.length === 0) {
							// don't leave empty
							const p = designer.createItem("p");
							parent.content.push(p);
							designer.setFocus(p);
						} else {
							designer.setFocus(undefined);
						}
						designer.refreshItem(parent);

					} else {
						tbl.rows.splice(i, 1);
						designer.setFocus((tbl.rows.length > i) ? tbl.rows[i] : tbl.rows[tbl.rows.length-1]);
						designer.refreshItem(tbl);

					}

				}}
			>Delete Row</button>
		);

		const cellContext = checkForParent("tableCell");
		if (cellContext) {
			buttons.push(
				<button
					key="delete-col"
					className="primary"
					onClick={() => {
						const cell = cellContext.def as TableCell;
						const row = cellContext.parent.def as TableRow;
						const tbl = cellContext.parent.parent.def as Table;
	
						// if this is the last col then delete tbl
						if (tbl.cols.length === 1) {
							designer.deleteItem(tbl);
							return;
						}

						const index = row.cells.indexOf(cell);
						// remove col def from table
						tbl.cols.splice(index, 1);
						// remove cells of this column through all rows
						for(let r of tbl.rows) {
							r.cells.splice(index, 1);
						}

						designer.setFocus((row.cells.length > index) ? row.cells[index] : row.cells[row.cells.length-1]);
						designer.refreshItem(tbl);
	
					}}
				>Delete Column</button>
			);		
		}
	}

	if (elementContext && elementContext?.parent && elementContext.parent.def && (elementContext.parent.def as any).content) {
		buttons.push(
			<button
				key="wrap-children-group"
				className="secondary"
				onClick={() => {
					const g = designer.createItem("group") as Group;
					g.content = [focused];

					const parentContent = (elementContext.parent.def as any).content;
					const i = parentContent.indexOf(focused);
					if (i >= 0) {
						parentContent.splice(i, 1, g);
					}

					designer.setFocus(g);

					const p = elementContext?.parent;
					if (p && p.def) {
						designer.refreshItem(p.def);
					} else {
						designer.refreshRoot();						
					}
				}}
			>Wrap with Group</button>,

			<button
				key="move-up"
				className="secondary"
				onClick={() => {
					const parentContent = (elementContext.parent.def as any).content;
					const i = parentContent.indexOf(focused);
					if (i >= 1) {
						moveArrayElement(parentContent, i, i-1);
					}
					const p = elementContext?.parent;
					if (p && p.def) {
						designer.refreshItem(p.def);
					} else {
						designer.refreshRoot();						
					}
				}}
			>Move Up</button>,

			<button
				key="move-down"
				className="secondary"
				onClick={() => {
					const parentContent = (elementContext.parent.def as any).content;
					const i = parentContent.indexOf(focused);
					if (i < (parentContent.length-1)) {
						moveArrayElement(parentContent, i, i+1);
					}
					const p = elementContext?.parent;
					if (p && p.def) {
						designer.refreshItem(p.def);
					} else {
						designer.refreshRoot();						
					}
				}}
			>Move Down</button>,
		)
	}


	if (focused && (focused as any).content) {
		buttons.push(
			<button
				key="wrap-children-group"
				className="secondary"
				onClick={() => {
					const g = designer.createItem("group") as Group;
					g.content = [...(focused as any).content];
					(focused as any).content = [g];

					designer.setFocus(g);
					designer.refreshItem(focused);
				}}
			>Wrap children in Group</button>
		)
	}


	if (focused.type === "group") {
		buttons.push(
			<button
				key="remove-group"
				className="secondary"
				onClick={() => {
					// all content under the group needs to be re-inserted back within parent
					const parentContent = (elementContext!.parent.def as any).content;
					const i = parentContent.indexOf(focused);
					if (i >= 0) {
						parentContent.splice(i, 1, ...(focused as Group).content);
					}

					designer.setFocus(parentContent[i]);

					const p = elementContext?.parent;
					if (p && p.def) {
						designer.refreshItem(p.def);
					} else {
						designer.refreshRoot();						
					}
				}}
			>Remove Group</button>
		)
	}

	buttons.push(
		<button
			key="duplicate"
			className="primary"
			onClick={() => {
				const context = designer.getContext(focused.id!);
				if (!context) return;
				const parent = context.parent.def as any;

				const srcRoot = context.def;
				const destRoot = duplicateElement(designer, srcRoot);

				// root
				const i = parent.content.indexOf(srcRoot);
				if (i >= 0) {
					parent.content.splice(i+1, 0, destRoot);
					designer.refreshItem(parent);
					setFocused(destRoot);
				}

				
			}}
		>Duplicate</button>,
	);

	buttons.push(
		<button
			key="copy"
			className="primary"
			onClick={() => {
				const context = designer.getContext(focused.id!);
				if (!context) return;

				const cb = clipboardContents.current;
				if (cb.action !== "copy") {
					cb.action = "copy";
					cb.items = [];
				}
				cb.items.push(context);
			}}
		>Copy</button>,
	);
	buttons.push(
		<button
			key="cut"
			className="primary"
			onClick={() => {
				const context = designer.getContext(focused.id!);
				if (!context) return;

				const cb = clipboardContents.current;
				if (cb.action !== "cut") {
					cb.action = "cut";
					cb.items = [];
				}
				cb.items.push(context);
			}}
		>Cut</button>,
	);
	if (clipboardContents.current.items.length > 0) {
		buttons.push(
			<button
				key="paste"
				className="primary"
				onClick={() => {
					const context = designer.getContext(focused.id!);
					if (!context || !context.parent || !context.parent.def) return;

					const parentContent = (context.parent.def as any).content;
					if (!parentContent) return;

					debugger;
					let index = parentContent.indexOf(context.def) + 1;

					const cb = clipboardContents.current;

					if (cb.action === "copy") {
						for(let item of cb.items) {
							const newItem = duplicateElement(designer, item.def);
							parentContent.splice(index, 0, newItem);
							index++;
						}

						designer.refreshItem(context.parent.def);
					
					} else if (cb.action === "cut") {
						for(let srcItem of cb.items) {
							const srcContent = (srcItem.parent.def as any).content;
							const srcIndex = srcContent.indexOf(srcItem.def);

							// move to new parent
							parentContent.splice(index, 0, srcItem.def);

							// remove from original parent
							srcContent.splice(srcIndex, 1);

							index++;
						}

						cb.items = [];
						designer.refreshRoot();

					}

				}}
			>Paste {<span className="count primary">{clipboardContents.current.items.length}</span>}</button>,
			<button
				key="paste"
				className="primary"
				onClick={() => {
					const cb = clipboardContents.current;
					cb.items = [];
					designer.refreshRoot();
				}}
			>Clear Clipboard</button>,
		);
	}

	if (hasDelete) {
		buttons.push(
			<button
				key="delete"
				className="primary"
				onClick={() => {
					debugger;

					const context = designer.getContext(focused.id!);
					if (!context) return;
					const parent = context.parent.def as any;
					
					let arr;
					if (parent.content) arr = parent.content;
					if (parent.items) arr = parent.items;
					const i = arr.indexOf(focused);

					if (i >= 0) arr.splice(i, 1);

					// is this last content item in parent?
					if (
						parent.content
						&& (arr.length === 0)
						&& (
							(designer.structure.layoutType === "dynamic")
							||
							(
								// don't want paragraphs added in static layout especially for sections
								parent.type !== "section"
								&& designer.structure.layoutType == "static"
							)
						)
					) {
						// add a paragraph back in
						const p = designer.createItem("p");
						arr.push(p);
						designer.setFocus(p);
					} else {
						designer.setFocus(undefined);
					}

					designer.refreshItem(parent);
				}}
			>Delete</button>
		);
	}

	if (buttons.length > 0) {
		fields.push(
			<div className="prop-group actions">
				<b>Actions</b>
				<div className="flex vert gap-2 align-start">{buttons}</div>
			</div>
		);
	}


	function addMoveButtons(parentArray: any[]) {
		fields.push(
			<button
				key="move-up"
				className="secondary"
				onClick={() => {
					const i = parentArray.indexOf(focused);
					if (i >= 1) {
						moveArrayElement(parentArray, i, i-1);
					}
					const p = elementContext?.parent;
					if (p && p.def) {
						designer.refreshItem(p.def);
					} else {
						designer.refreshRoot();						
					}
				}}
			>Move Up</button>,

			<button
				key="move-down"
				className="secondary"
				onClick={() => {
					const i = parentArray.indexOf(focused);
					if (i < (parentArray.length-1)) {
						moveArrayElement(parentArray, i, i+1);
					}
					const p = elementContext?.parent;
					if (p && p.def) {
						designer.refreshItem(p.def);
					} else {
						designer.refreshRoot();						
					}
				}}
			>Move Down</button>,
		);

	}
	if (focused && focused.type === "tableRow") {
		const rows = (elementContext?.parent.def as Table).rows;
		if (rows) {
			addMoveButtons(rows);
		}
	}


	return (
		<DataContext.Provider value={dataContext}>
			<ElementRefresher />

			<div className="inspector">
				<h3>{getTypeName(focused.type!)}</h3>

				{fields}

				{docDefaultsModal.render()}
			</div>
		</DataContext.Provider>
	);
}

function AlignValue(props: {
	item: Content,
	prop: string,
}) {
	const designer = useDesigner();
	const data = useData<any>();

	return (
		<div className="flex">
			<label>Align</label>
			<div className="margin-top-2 flex gap-5">
				<button
					className="padding-0 transparent"
					key="alignLeft"
					onClick={() => {
						delete data.value[props.prop];
						designer.refreshItem(props.item);
					}}
				><Icon type="alignLeft" /></button>
				<button
					className="padding-0 transparent"
					key="alignCenter"
					onClick={() => {
						data.setValue(props.prop, "center");
						designer.refreshItem(props.item);
					}}
				><Icon type="alignCenter" /></button>
				<button
					className="padding-0 transparent"
					key="alignRight"
					onClick={() => {
						data.setValue(props.prop, "right");
						designer.refreshItem(props.item);
					}}
				><Icon type="alignRight" /></button>
				<button
					className="padding-0 transparent"
					key="alignJustify"
					onClick={() => {
						data.setValue(props.prop, "justified");
						designer.refreshItem(props.item);
					}}
				><Icon type="alignJustify" /></button>
			</div>
		</div>
	)
}

function HeadingLevelValue(props: {
	item: Content,
}) {
	const designer = useDesigner();
	const data = useData<any>();
	const current = useDataValue(data, "level");

	return (
		<div className="flex">
			<label>Level</label>
			<div className="margin-top-2 flex gap-5">
				{[1, 2, 3, 4, 5].map(n => (
					<button
						key={n}
						className="padding-3 transparent"
						style={current === n ? { backgroundColor: "var(--accent, #4a90d9)", color: "#fff", borderRadius: "3px" } : undefined}
						onClick={() => {
							data.setValue("level", n);
							designer.refreshItem(props.item);
						}}
					>{n}</button>
				))}
			</div>
		</div>
	)
}

function StandardColors(props: {

}) {
	return (
		<div>
			<b>Colors</b>
			<ColorValue prop="bgColor" key="bgColor" type="Background" />
			<ColorValue prop="color" key="color" type="Foreground" />
		</div>
	);
}

function ColorValue(props: {
	prop: string,
	type: string,
}) {
	const data = useData<any>();
	let color = data.getValue(props.prop);
	if (!color) color = "#ffffff";

	return (
		<div className="flex horiz">
			<span>{props.type}</span>
			<input
				type="color"
				value={color}
				onChange={(e) => {
					data.setValue(props.prop, e.target.value, true);
				}}
			/>
			<button className="transparent" onClick={() => {
				data.setValue(props.prop, "", true);
			}}>Clear</button>
		</div>
	)
}

function MarginValue(props: {
	prop: string,
	type?: string,
}) {

	const typeName = props.type ? props.type : "Margin";

	return (
		<div className="prop-group">
			<b>{typeName}</b>
			<div>
				<MillimeterValue prop={props.prop + ".top"} title="Top" key={props.prop + ".top"} />
				<MillimeterValue prop={props.prop + ".right"} title="Right" key={props.prop + ".right"} />
				<MillimeterValue prop={props.prop + ".bottom"} title="Bottom" key={props.prop + ".bottom"} />
				<MillimeterValue prop={props.prop + ".left"} title="Left" key={props.prop + ".left"} />
			</div>
		</div>
	)
}

function BorderValue(props: {
	prop: string,
}) {
	return (
		<>
			<MillimeterValue prop={props.prop + ".width"} title="Border Width" key={props.prop + ".width"} />
		</>
	)
}

function TextValue(props: TextProps) {
	return (
		<Text
			{...props}
			style={{textAlign: "center"}}
		/>
	)
}

function NumberValue(props: TextProps) {
	return (
		<Text
			{...props}
			style={{textAlign: "center"}}

			onChangeValue={(val) => {
				const n = toNumber(val);
				if (isValidNumber(n)) return n;
				return 0; 
			}}
		/>
	)
}

function PercentValue(props: TextProps) {

	return (
		<Text
			{...props}
			
			typeIndicator="percent"
			indicatorSide="right"

			onChangeValue={(newValue) => {
				let p = Number(newValue);
				if (isNaN(p)) return undefined;

				return Math.round(p);
			}}
			onFormatValue={(value: string) => {
				let p = Number(value);
				if (isNaN(p)) return "";
				return String(Math.round(p));
			}}

		/>
	)
}

function MillimeterValue(props: {
		units?: string,
	} & TextProps
) {

	const units = props.units || "mm";

	return (
		<Text
			{...props}
			
			typeIndicator={units}
			indicatorSide="right"

			onChangeValue={(newValue) => {
				let mm = Number(newValue);
				if (!newValue.endsWith(".") && !isNaN(mm)) {
					return Math.round(toPdfPoint(mm) * 10) / 10;
				} else {
					return newValue;
				}
			}}
			onFormatValue={(value: string) => {
				console.log(`format: ` + value);
				value = String(value);
				if (value && value.endsWith(".")) return value;
				let point = Number(value);
				if (isNaN(point)) return ""; //point = 0;
				return String(Math.round(toMillimeters(point) * 10) / 10);
			}}

		/>
	)
}

function ElementRefresher() {
	const data = useData();
	const designer = useDesigner();

	useDataEvents(data, React.useCallback((msg) => {
		if (msg.type === DataMessages.CHANGE) {
			if (designer.focus) {
				designer.refreshItem(designer.focus);
			}
		}
	}, [designer]));

	return null;
}

export function ImageValue(props: {
	prop: string,
}) {

	const modal = useConditional(() => {
		return (
			<ChooseImage
				onClose={modal.close}
			/>
		)
	});

	const data = useData();
	const value = data.getValue(props.prop);

	let selected;
	if (!value) {
		selected = <i>None selected</i>;
	} else {
		selected = (
			<div className="flex horiz gap-5">
				<Icon type="document" />
				<span>{value.title}</span>
			</div>
		)
	}


	return (
		<div>
			{selected}

			<button
				className="primary"
				onClick={() => {
					modal.show({});
				}}
			>Choose</button>

			{modal.render()}
		</div>
	)
}

export function ChooseImage(props: {
	onClose: () => void,
}) {

	// for the image element
	const data = useData();


	const dataContext = useDataContext({});

	const presenterID = React.useRef("");
	if (!presenterID.current) presenterID.current = newID();

	return (
		<Modal
			title="Choose Image"
			size="width-50vw"
			onCancel={props.onClose}
			onSave={() => {
				// check if there are any errors? 
				const presenter = getPresenter(presenterID.current);
				const ok = presenter.validateAll();
				if (!ok) return;

				const img = presenter.findValue("image");
				data.setValue("image", img);

				const url = api.getDownloadSystemUrl(img.blob)
				getImgSize(url).then((sz: any) => {
					data.setValue("width", sz.width);
					data.setValue("height", sz.height);
				});

				props.onClose();
			}}
		>

			<DataContext.Provider value={dataContext}>
				<InlinePresenter
					id={presenterID.current}
					formID="choose-image"
				/>
			</DataContext.Provider>

		</Modal>
	)
}